import React, { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import posthog from 'posthog-js'
import { AuthProvider } from './contexts/AuthContext.jsx'
import { SubscriptionProvider } from './contexts/SubscriptionContext.jsx'
import { DiscoveryProvider } from './contexts/DiscoveryContext.jsx'
import { MonitorProvider } from './contexts/MonitorContext.jsx'
import { ToastProvider } from './contexts/ToastContext.jsx'
import { ConfirmProvider } from './contexts/ConfirmContext.jsx'
import Sidebar from './components/Sidebar'
import FilingRail from './components/FilingRail'
import { FilingRailProvider } from './contexts/FilingRailContext.jsx'
import { captureReferralFromUrl } from './utils/referral.js'
import { hasMobileUserAgent } from './utils/device.js'
import { trackPageView } from './utils/metaPixel.js'
import { restoreHandoffFromUrl } from './utils/handoff.js'
import ProtectedRoute from './components/ProtectedRoute.jsx'
import MobileBlocker from './components/MobileBlocker.jsx'
import ErrorBoundary from './components/ErrorBoundary.jsx'
import RouteFallback from './components/RouteFallback.jsx'
import GlobalUpgradePrompt from './components/GlobalUpgradePrompt.jsx'
import PaymentFailedBanner from './components/PaymentFailedBanner.jsx'
import FilingReminderModal from './components/FilingReminderModal.jsx'
import CompleteProfileModal from './components/CompleteProfileModal.jsx'
import { useVersionCheck } from './hooks/useVersionCheck'
import './App.css'

// Lazy-loaded pages for code splitting
const Profile = lazy(() => import('./pages/Profile'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const EditPatent = lazy(() => import('./pages/EditPatent'))
const Search = lazy(() => import('./pages/Search'))
const NewPatentApplication = lazy(() => import('./pages/NewPatentApplication'))
const Step2_1 = lazy(() => import('./pages/Step2_1'))
const Step2_2 = lazy(() => import('./pages/Step2_2'))
const Step2_3 = lazy(() => import('./pages/Step2_3'))
const ReviewApplication = lazy(() => import('./pages/ReviewApplication'))
const FileApplication = lazy(() => import('./pages/FileApplication'))
const Step3 = lazy(() => import('./pages/Step3'))
// Manufacturing parked (2026-07): platform refocused on IP protection. Code kept for later.
// const Step4 = lazy(() => import('./pages/Step4'))
const Login = lazy(() => import('./pages/Login'))
const Signup = lazy(() => import('./pages/Signup'))
const Onboarding = lazy(() => import('./pages/Onboarding/Onboarding'))
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'))
const ConfirmCollaboration = lazy(() => import('./pages/ConfirmCollaboration'))
const AbandonedPatentExplorer = lazy(() => import('./pages/AbandonedPatentExplorer'))
const ExplorerWall = lazy(() => import('./pages/ExplorerWall'))
const ExplorerFullRecord = lazy(() => import('./pages/ExplorerFullRecord'))
const ExplorerReinvent = lazy(() => import('./pages/ExplorerReinvent'))
const PatentDetail = lazy(() => import('./pages/PatentDetail'))
const MyIdeas = lazy(() => import('./pages/MyIdeas'))
const NewIdea = lazy(() => import('./pages/NewIdea'))
const Paywall = lazy(() => import('./pages/Paywall'))
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'))
const SubscriptionSuccess = lazy(() => import('./pages/SubscriptionSuccess'))
// Manufacturing parked (2026-07): platform refocused on IP protection. Code kept for later.
// const Manufacturing = lazy(() => import('./pages/Manufacturing'))
const Learn = lazy(() => import('./pages/Learn'))
const Monitor = lazy(() => import('./pages/Monitor'))
const CompetitorDossier = lazy(() => import('./pages/CompetitorDossier'))
const MonitorSetup = lazy(() => import('./pages/MonitorSetup'))
const CreatorDashboard = lazy(() => import('./pages/CreatorDashboard'))

/**
 * Every route opens scrolled to the top, except these.
 *
 * React Router doesn't reset scroll on navigation, so arriving at a page from a
 * scrolled-down position landed you part-way down it. The explorer is excluded on
 * purpose: it's an infinite-scroll list, and resetting it would throw away your place
 * every time you came back from a patent detail view.
 */
const SCROLL_TO_TOP_EXCLUDED_PREFIXES = ['/abandoned-patents-legacy']

/**
 * Same idea, but matched exactly rather than by prefix.
 *
 * `/wall` keeps your place when you come back from a patent's full record. `/wall/patent/:id`
 * must NOT — arriving at a fresh record part-way down the page would be wrong — so a prefix
 * match would be too blunt here.
 */
const SCROLL_TO_TOP_EXCLUDED_EXACT = ['/abandoned-patents']

function AppContent() {
  // Check for new deployments and auto-reload if needed
  useVersionCheck()

  const location = useLocation()

  // useLayoutEffect so the reset happens before paint — with useEffect the new step
  // renders at the old offset for a frame and visibly jumps.
  useLayoutEffect(() => {
    const excluded = SCROLL_TO_TOP_EXCLUDED_PREFIXES.some((p) => location.pathname.startsWith(p))
      || SCROLL_TO_TOP_EXCLUDED_EXACT.includes(location.pathname)

    // Client-side navigation is only half of it. On a full page LOAD — the Google
    // sign-in redirect fallback, the version-check auto-reload, or an ordinary refresh —
    // the browser restores the old scroll position itself, asynchronously, after content
    // renders. That lands after any reset we do, so it wins. Marking the history entry
    // `manual` stops the browser restoring at all and leaves us authoritative.
    //
    // Flipped per-route rather than set once: the explorer WANTS the browser's
    // restoration, since that is what returns you to your place in the infinite list
    // after a detour into a patent detail view.
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = excluded ? 'auto' : 'manual'
    }

    if (excluded) return
    window.scrollTo(0, 0)
  }, [location.pathname])

  // A creator's link lands on whatever page they put in their bio — usually the bare domain,
  // not /signup. Capturing here means ?ref= is remembered wherever it arrives, and survives
  // the walk to the signup form and the Google redirect off-site and back.
  useEffect(() => {
    captureReferralFromUrl(location.search)
    /*
     * The other half of the in-app browser handoff. Somebody who left TikTok for a real
     * browser arrives here with their onboarding answers in the URL, because sessionStorage
     * did not come with them -- without this they would answer everything again. The referral
     * rides as ?ref= and is picked up by the line above, which is why the handoff link uses
     * that parameter rather than inventing one.
     */
    restoreHandoffFromUrl(location.search)
  }, [location.search])

  // '/' belongs here even though it renders no page of its own. It only redirects to /login,
  // but from inside the MobileBlocker branch that redirect never executed on a phone: the
  // blocker rendered first, so someone arriving at the bare domain on a phone hit "Desktop
  // Required" with no way through to the login box or the signup form, and no way to leave
  // us an email address. Destination is unchanged; this only lets it happen.
  /*
   * The paywall is an auth page everywhere except a phone.
   *
   * Listing it here put it outside MobileBlocker, so a new account created on a phone was
   * routed straight to it by ProtectedRoute and asked to pay -- on a device that shows
   * "Desktop Required" the moment the payment clears. That is the highest-refund shape there
   * is, and a chargeback on a referred sale also reverses the creator's commission. It also
   * spends day one of a three-day trial before the person has seen anything.
   *
   * On a phone the desktop screen wins instead, and it now has somewhere to go: the App Store
   * on iOS, an emailed link on Android. The paywall is waiting on the machine that can
   * actually use what it sells.
   *
   * The user-agent test, not the viewport one: somebody on a narrow desktop window can widen
   * it and pay, so they should still meet the paywall rather than be told to find a computer
   * they are already sitting at.
   */
  const paywallIsAuthPage = location.pathname === '/paywall' && !hasMobileUserAgent()

  const isAuthPage = location.pathname === '/' || paywallIsAuthPage || location.pathname === '/login' || location.pathname === '/signup' || location.pathname === '/onboarding' || location.pathname === '/verify-email' || location.pathname === '/forgot-password' || location.pathname === '/reset-password' || location.pathname === '/invites/accept' || location.pathname === '/confirm-collaboration'

  // The sidebar now has two states: labelled (default) and the slim icon rail.
  // Lives here rather than inside Sidebar because `.main-content`'s offset keys off the
  // same flag — the rail and the content it displaces have to agree.
  // Remembered across sessions: a nav collapsing itself again on every load is a bug.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebarCollapsed') === 'true' } catch { return false }
  })

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev
      try { localStorage.setItem('sidebarCollapsed', String(next)) } catch { /* private mode */ }
      return next
    })
  }, [])

  /*
   * Meta pageviews. One per route, first render included — the pixel's own snippet would
   * report a single pageview for the whole session, this being a single-page app.
   * PostHog gets the same treatment via capture_pageview: 'history_change' in main.jsx.
   */
  useEffect(() => {
    trackPageView()
  }, [location.pathname])

  useEffect(() => {
    const handleAppExit = () => {
      posthog.capture('app_exit', { exit_page: location.pathname })
    }
    window.addEventListener('beforeunload', handleAppExit)
    return () => window.removeEventListener('beforeunload', handleAppExit)
  }, [location.pathname])

  // Auto-reload mechanism when user returns after being away
  useEffect(() => {
    let hiddenTime = null;
    const INACTIVITY_THRESHOLD = 30 * 60 * 1000; // 30 minutes

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden, record the time
        hiddenTime = Date.now();
      } else {
        // Page is visible again
        if (hiddenTime) {
          const timeAway = Date.now() - hiddenTime;

          // If user was away for more than threshold, reload
          if (timeAway > INACTIVITY_THRESHOLD) {
            console.log('User was away for a long time, reloading page...');
            window.location.reload();
          }

          hiddenTime = null;
        }
      }
    };

    // Add event listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // A creator's earnings page. Its own branch because it is neither an app page nor an auth
  // page: no sidebar, no login, and it must open on a phone — the person checking it posts
  // from one — so it cannot sit behind the desktop wall.
  if (location.pathname.startsWith('/r/')) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/r/:token" element={<CreatorDashboard />} />
        </Routes>
      </Suspense>
    )
  }

  // Auth pages (login/signup/verify-email/invites/confirmations) - no sidebar
  if (isAuthPage) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Carries the query across. A bare `to="/login"` dropped it, and the bare domain
              is exactly where a creator's link lands: autoinvent.app/?ref=CODE arrived, this
              redirect fired, and the code was gone before anything could read it — the
              referral was never attributed and the creator was never paid for the signup.
              Same for utm tags and anything else a campaign appends. */}
          <Route path="/" element={<Navigate to={`/login${location.search}${location.hash}`} replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/onboarding" element={<Onboarding />} />
          {/*
            Full screen, with no sidebar — deliberately in this branch rather than the shell.
            A hard paywall that renders a nav rail is offering links to Dashboard, Monitoring
            and New Application that the person cannot open, which makes the wall look like a
            bug rather than a choice. The only way onward is a plan, and the only way out is
            the sign-out pill on the page itself.

            Still ProtectedRoute: the screen says "Signed in as" and needs an account behind it.
          */}
          <Route path="/paywall" element={<ProtectedRoute><Paywall /></ProtectedRoute>} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/invites/accept" element={<AcceptInvite />} />
          <Route path="/confirm-collaboration" element={<ConfirmCollaboration />} />
        </Routes>
      </Suspense>
    )
  }

  /*
   * Filing is a focus mode: on /new-patent/* the step rail takes the sidebar's place rather
   * than sitting beside it. Two navigation columns would crowd the form and split attention
   * between the filing and the rest of the product. See FilingRail.jsx.
   */
  const isFiling = location.pathname.startsWith('/new-patent')

  // All other pages - with sidebar (blocked on mobile)
  return (
    <MobileBlocker>
      <FilingRailProvider>
      <div className={`app ${sidebarCollapsed ? 'sidebar-collapsed' : ''}${isFiling ? ' filing-mode' : ''}`}>
        {isFiling ? <FilingRail /> : <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />}
        <main className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <PaymentFailedBanner />
          <FilingReminderModal />
          <GlobalUpgradePrompt />
          {/* Mounted on the whole authed shell, not just /dashboard, so it can't be
              sidestepped by navigating straight to another route. */}
          <CompleteProfileModal />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* No "/" route here: it is handled in the branch above, which is what keeps it
                reachable on a phone. Adding one back would be dead code. */}
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            {/* One plans page. /paywall carries Free, Pro and Premium; the old Free/Pro page never learned Premium. */}
            <Route path="/pricing" element={<Navigate to="/paywall" replace />} />
            <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
            <Route path="/subscription-success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
            <Route path="/ideas" element={<ProtectedRoute><MyIdeas /></ProtectedRoute>} />
            <Route path="/new-idea" element={<ProtectedRoute><NewIdea /></ProtectedRoute>} />
            <Route path="/edit-idea/:id" element={<ProtectedRoute><NewIdea /></ProtectedRoute>} />
            <Route path="/search" element={<ProtectedRoute><Search /></ProtectedRoute>} />
            {/* The Explorer. `/wall` was an internal name that had leaked into the address
                bar; this is the URL a user would guess or bookmark, and it matches the
                sidebar label. Requires EXPLORER_WALL_ENABLED=true on the API or every
                /api/wall call 404s (routes.ts:311).
                Internals keep the "wall" name — service, component, CSS, env flag — since
                renaming those is churn with no user-visible benefit. */}
            <Route path="/abandoned-patents" element={<ProtectedRoute><ExplorerWall /></ProtectedRoute>} />
            <Route path="/abandoned-patents/patent/:patentId" element={<ProtectedRoute><ExplorerFullRecord /></ProtectedRoute>} />
            <Route path="/abandoned-patents/reinvent/:patentId" element={<ProtectedRoute><ExplorerReinvent /></ProtectedRoute>} />
            {/* The superseded grid, kept mounted but unlinked. EXPLORER_WALL_ENABLED is a
                kill switch, and with the old page deleted there would be nothing to fall
                back TO. Delete this route and AbandonedPatentExplorer.jsx once the wall has
                held in production. */}
            <Route path="/abandoned-patents-legacy" element={<ProtectedRoute><AbandonedPatentExplorer /></ProtectedRoute>} />
            {/* Manufacturing parked (2026-07) — route dormant, page code kept for later.
            <Route path="/manufacturing" element={<ProtectedRoute><Manufacturing /></ProtectedRoute>} /> */}
            <Route path="/patent/:id" element={<ProtectedRoute><PatentDetail /></ProtectedRoute>} />
            <Route path="/new-patent" element={<ProtectedRoute><NewPatentApplication /></ProtectedRoute>} />
            <Route path="/new-patent/step-2-1" element={<ProtectedRoute><Step2_1 /></ProtectedRoute>} />
            <Route path="/new-patent/step-2-2/:patentId" element={<ProtectedRoute><Step2_2 /></ProtectedRoute>} />
            <Route path="/new-patent/step-2-3" element={<ProtectedRoute><Step2_3 /></ProtectedRoute>} />
            <Route path="/new-patent/review" element={<ProtectedRoute><ReviewApplication /></ProtectedRoute>} />
            <Route path="/new-patent/file" element={<ProtectedRoute><FileApplication /></ProtectedRoute>} />
            <Route path="/new-patent/step-3" element={<ProtectedRoute><Step3 /></ProtectedRoute>} />
            {/* Manufacturing parked (2026-07) — route dormant, Step4 page code kept for later.
            <Route path="/new-patent/step-4" element={<ProtectedRoute><Step4 /></ProtectedRoute>} /> */}
            <Route path="/edit-patent/:id" element={<ProtectedRoute><EditPatent /></ProtectedRoute>} />
            <Route path="/learn" element={<ProtectedRoute><Learn /></ProtectedRoute>} />
            {/* Competitive-intelligence feed. Rendered for all authed users; the page itself
                shows an upgrade prompt unless the user is on Premium ('business') — the
                entitlement check IS the gate (plan doc §9). */}
            <Route path="/monitor" element={<ProtectedRoute><Monitor /></ProtectedRoute>} />
            <Route path="/monitor/new" element={<ProtectedRoute><MonitorSetup /></ProtectedRoute>} />
            <Route path="/monitor/entity/:entityId" element={<ProtectedRoute><CompetitorDossier /></ProtectedRoute>} />
          </Routes>
          </Suspense>
        </main>
      </div>
      </FilingRailProvider>
    </MobileBlocker>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SubscriptionProvider>
          <DiscoveryProvider>
            <MonitorProvider>
              <ToastProvider>
                <ConfirmProvider>
                  <Router>
                    <AppContent />
                  </Router>
                </ConfirmProvider>
              </ToastProvider>
            </MonitorProvider>
          </DiscoveryProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App